import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { TypingDots } from '@/components/glitch-ui/typing-dots';
import { landingPageEditMessagesQueryOptions } from '@/lib/query-options';
import { submitVibeEdit } from '@/lib/apis';
import { toast } from '@/hooks/useToast';
import type { LandingPageEditMessage } from '@/lib/types';

const MIN_INSTRUCTION = 3;
const MAX_INSTRUCTION = 1000;

interface VibeEditChatProps {
  publicId: string;
  organizationId: number;
  editInProgress: boolean;
  onSubmitted: () => void;
}

export function VibeEditChat({ publicId, organizationId, editInProgress, onSubmitted }: VibeEditChatProps) {
  const queryClient = useQueryClient();
  const [instruction, setInstruction] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: messages = [] } = useQuery(
    landingPageEditMessagesQueryOptions(publicId, organizationId, editInProgress),
  );

  const mutation = useMutation({
    mutationFn: (text: string) => submitVibeEdit(publicId, organizationId, text, null),
    onSuccess: () => {
      setInstruction('');
      // The backend persisted the user turn before returning 202; refetch so it (then the reply) appear.
      queryClient.invalidateQueries({ queryKey: ['landing-page-messages', publicId] });
      onSubmitted();
    },
    onError: (error) => {
      toast({
        title: 'Failed to submit edit',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const busy = editInProgress || mutation.isPending;
  const canSubmit = instruction.trim().length >= MIN_INSTRUCTION && !busy;

  // Keep the newest turn (and the typing indicator) in view as the thread grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, busy]);

  const submit = () => {
    if (canSubmit) mutation.mutate(instruction.trim());
  };

  return (
    <div className="rounded-lg border bg-white">
      <div className="flex items-center gap-2 border-b px-4 py-3 text-gray-900">
        <Sparkles className="h-4 w-4 text-[#290BD9]" />
        <span className="font-medium">Vibe Edit</span>
      </div>

      <div ref={scrollRef} className="max-h-80 space-y-3 overflow-y-auto px-4 py-4">
        {messages.length === 0 && !busy && (
          <p className="text-sm text-gray-500">
            Describe a change and it will be applied to your page. For example: "Make the hero headline shorter and
            change the button to Get My Free Quote".
          </p>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {/* Optimistic echo: the request is in flight but the persisted user turn hasn't been refetched yet. */}
        {mutation.isPending && mutation.variables && <Bubble align="right">{mutation.variables}</Bubble>}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3">
              <TypingDots />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-end gap-2 border-t px-4 py-3">
        <Textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value.slice(0, MAX_INSTRUCTION))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Describe a change…"
          disabled={busy}
          rows={2}
          className="resize-none"
        />
        <Button
          onClick={submit}
          disabled={!canSubmit}
          className="h-9 shrink-0 bg-[#290BD9] text-white hover:bg-[#2309C5]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
        </Button>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: LandingPageEditMessage }) {
  if (message.role === 'user') {
    return <Bubble align="right">{message.body}</Bubble>;
  }
  // A failed edit left the live page untouched; surface the reason in error colours.
  const failed = message.status === 'failed';
  return (
    <div className="flex justify-start">
      <div
        className={`max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-2 text-sm ${
          failed ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-gray-100 text-gray-900'
        }`}
      >
        <MarkdownContent content={message.body} />
      </div>
    </div>
  );
}

function Bubble({ align, children }: { align: 'left' | 'right'; children: ReactNode }) {
  const right = align === 'right';
  return (
    <div className={`flex ${right ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
          right ? 'rounded-tr-sm bg-[#290BD9] text-white' : 'rounded-tl-sm bg-gray-100 text-gray-900'
        }`}
      >
        {children}
      </div>
    </div>
  );
}
